import { CheckCircle2, Loader2, X, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { CoverageStatusBadge } from '@/components/coverage/CoverageStatusBadge';
import { categoryBadgeConfig } from '@/components/coverage/shared/constants';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import { useVerifyFirstPartyCoverageMutation } from '@/services/api/tasksService';
import type { CoverageListItem } from '@/services/types/coverage-types';
import {
  ClientDecision,
  CoverageCategory,
  CoverageStatus,
} from '@/services/types/coverage-types';

const FIRST_PARTY_CATEGORIES = new Set<CoverageCategory>([
  CoverageCategory.FIRST_PARTY,
  CoverageCategory.FIRST_PARTY_HEALTH,
]);

const HIDDEN_STATUSES = new Set<CoverageStatus | null>([
  CoverageStatus.NOT_APPLICABLE,
  CoverageStatus.DUPLICATE,
]);

type Decision = 'YES' | 'NO';

/** Map existing client_decision to the modal's YES/NO decision type. */
const clientDecisionToDecision = (
  cd: ClientDecision | null | undefined,
): Decision | undefined => {
  if (cd === ClientDecision.APPROVED) return 'YES';
  if (cd === ClientDecision.DECLINED) return 'NO';
  return undefined;
};

interface Verify1PCoverageModalProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  taskId: string;
}

export const Verify1PCoverageModal = ({
  isOpen,
  onClose,
  matterId,
  taskId,
}: Verify1PCoverageModalProps) => {
  const {
    data: coverages,
    isLoading: isCoveragesLoading,
    isError: isCoveragesError,
  } = useGetCoveragesQuery({ matterId }, { skip: !isOpen });

  const [verifyFirstPartyCoverage, { isLoading: isSubmitting }] =
    useVerifyFirstPartyCoverageMutation();

  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // All non-terminal first-party coverages that still need a decision.
  // Coverages with an existing claim number are excluded — the claim was
  // already opened so forcing a decision is counterproductive.
  // Sorted so coverages without a decision appear first.
  const firstPartyCoverages = useMemo(() => {
    if (!coverages) return [];
    return coverages
      .filter(
        (c): c is CoverageListItem =>
          c.category !== null &&
          FIRST_PARTY_CATEGORIES.has(c.category) &&
          !HIDDEN_STATUSES.has(c.status) &&
          !c.claims?.some((cl) => Boolean(cl.claim_identifier?.trim())),
      )
      .sort((a, b) => {
        const aHasDecision = a.client_decision != null ? 1 : 0;
        const bHasDecision = b.client_decision != null ? 1 : 0;
        return aHasDecision - bHasDecision;
      });
  }, [coverages]);

  // Pre-populate decisions from existing client_decision values.
  // Uses functional update so a background refetch won't wipe in-progress user choices.
  useEffect(() => {
    if (firstPartyCoverages.length === 0) return;
    setDecisions((prev) => {
      const next = { ...prev };
      let changed = false;
      firstPartyCoverages.forEach((c) => {
        if (next[c.id] !== undefined) return; // Don't overwrite user's in-progress choice
        const existing = clientDecisionToDecision(c.client_decision);
        if (existing) {
          next[c.id] = existing;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [firstPartyCoverages]);

  const allDecided =
    firstPartyCoverages.length > 0 &&
    firstPartyCoverages.every((c) => decisions[c.id] !== undefined);

  // Only coverages whose decision changed from the original
  const hasChanges = useMemo(() => {
    return firstPartyCoverages.some((c) => {
      const original = clientDecisionToDecision(c.client_decision);
      const current = decisions[c.id];
      return current !== undefined && current !== original;
    });
  }, [firstPartyCoverages, decisions]);

  const handleToggleDecision = useCallback(
    (coverageId: string, decision: Decision) => {
      setDecisions((prev) => {
        if (prev[coverageId] === decision) {
          const next = { ...prev };
          delete next[coverageId];
          return next;
        }
        return { ...prev, [coverageId]: decision };
      });
    },
    [],
  );

  const handleSubmit = async () => {
    // Only submit coverages whose decision differs from the existing client_decision
    const decisionItems = firstPartyCoverages
      .filter((c) => {
        const original = clientDecisionToDecision(c.client_decision);
        const current = decisions[c.id];
        return current !== undefined && current !== original;
      })
      .map((c) => ({
        coverage_id: c.id,
        decision: decisions[c.id],
      }));

    if (decisionItems.length === 0) {
      onClose();
      return;
    }

    try {
      await verifyFirstPartyCoverage({
        matterId,
        taskId,
        body: { decisions: decisionItems },
      }).unwrap();
      toast.success('1P coverage verification submitted');
      onClose();
    } catch {
      toast.error('Failed to submit 1P coverage verification');
    }
  };

  const getCarrierName = (coverage: CoverageListItem): string =>
    coverage.carrier?.name ?? 'Unknown Carrier';

  const getPolicyNumber = (coverage: CoverageListItem): string | null => {
    const policyId = coverage.policy_identifiers?.find(
      (p) => p.identifier_type === 'POLICY_NUMBER',
    );
    return policyId?.identifier_value ?? null;
  };

  if (!isOpen) return null;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
        onClick={onClose}
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Verify 1P Coverage"
          className="w-full max-w-xl rounded-lg border bg-white p-8 shadow-lg"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        >
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold leading-none tracking-tight">
                Verify 1P Coverage
              </h2>
              <p className="mt-1.5 text-sm font-medium text-gray-500">
                Confirm with the client whether to open each coverage.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm p-1 opacity-70 transition-opacity hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="space-y-4 pt-6">
            {isCoveragesLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
              </div>
            )}

            {!isCoveragesLoading && isCoveragesError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-center">
                <p className="text-sm font-medium text-red-700">
                  Failed to load coverages
                </p>
              </div>
            )}

            {!isCoveragesLoading &&
              !isCoveragesError &&
              firstPartyCoverages.length === 0 && (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-center">
                  <p className="text-sm font-medium text-gray-700">
                    No first-party coverages found
                  </p>
                </div>
              )}

            {firstPartyCoverages.map((coverage) => {
              const decision = decisions[coverage.id];
              const carrierName = getCarrierName(coverage);
              const policyNumber = getPolicyNumber(coverage);

              return (
                <div
                  key={coverage.id}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                >
                  {/* Coverage info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {carrierName}
                      </p>
                      {coverage.category && (
                        <Badge
                          variant="outline"
                          className="shrink-0 bg-white px-1.5 py-0 text-xs font-medium"
                        >
                          {categoryBadgeConfig[coverage.category]?.text ??
                            'Other'}
                        </Badge>
                      )}
                    </div>
                    <CoverageStatusBadge status={coverage.status} />
                  </div>
                  {policyNumber && (
                    <p className="mt-0.5 text-xs font-medium text-gray-500">
                      {policyNumber}
                    </p>
                  )}

                  {/* Decision area */}
                  <div className="mt-3 border-t border-gray-200 pt-3">
                    <p className="text-sm font-medium text-gray-700">
                      Does the client want to open this coverage?
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        type="button"
                        variant={decision === 'YES' ? 'default' : 'outline'}
                        size="sm"
                        className={
                          decision === 'YES'
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-white'
                        }
                        onClick={() =>
                          handleToggleDecision(coverage.id, 'YES')
                        }
                      >
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                        Yes, open
                      </Button>
                      <Button
                        type="button"
                        variant={decision === 'NO' ? 'default' : 'outline'}
                        size="sm"
                        className={
                          decision === 'NO'
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-white'
                        }
                        onClick={() =>
                          handleToggleDecision(coverage.id, 'NO')
                        }
                      >
                        <XCircle className="mr-1.5 h-3.5 w-3.5" />
                        No, decline
                      </Button>
                    </div>
                    {decision === 'YES' && (
                      <p className="mt-2 text-xs font-medium text-gray-500">
                        Coverage will be approved.
                      </p>
                    )}
                    {decision === 'NO' && (
                      <p className="mt-2 text-xs font-medium text-gray-500">
                        Client declined — no further tasks will be created.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-6">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!allDecided || !hasChanges || isSubmitting}
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Submit
            </Button>
          </div>
        </div>
      </div>
  );
};
